import axios, {AxiosPromise} from 'axios'
import {envVars} from '@/envVars.ts'
import {useAuthStore} from '@/features/auth/store/store.ts'
import {LOCAL_STORAGE_ACCESS_TOKEN, LOCAL_STORAGE_REFRESH_TOKEN} from '@/features/auth/constants.ts'
import {z, ZodError, ZodSchema} from 'zod'

const axiosConfig = {
    baseURL: envVars.VITE_API_ENDPOINT,
    headers: {
        common: {
            Authorization: localStorage.getItem('accessToken')
                ? `Bearer ${localStorage.getItem('accessToken')}`
                : false,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'ngrok-skip-browser-warning': '1'
        },
        post: {
            'Access-Control-Allow-Origin': '*'
        }
    }
}

// create axios custom instance with custom config
const axiosInstance = axios.create(axiosConfig)

const attemptRefresh = async (refreshToken: string) => {
    const url = '/auth/refresh'
    try {
        const {data} = await axiosInstance.post(url, {refreshToken})
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, data.accessToken)
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, data.refreshToken)
        useAuthStore.setState(() => ({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken
        }))

        return {data}
    } catch (error) {
        if (axios.isAxiosError(error)) {
            localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
            localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
            useAuthStore.getState().reset()
        } else {
            console.error(error)
        }
    }
}

axiosInstance.interceptors.request.use(request => {
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
    if (token) request.headers['Authorization'] = `Bearer ${token}`
    return request
})

const endpointToIgnore = ['/login']

axiosInstance.interceptors.response.use(
    response => {
        // pass response without change
        return response
    },
    async error => {
        // Handle blob errors - convert blob errors to JSON when possible
        if (error.config?.responseType === 'blob' && error.response?.data instanceof Blob) {
            try {
                const errorText = await error.response.data.text()
                const errorJson = JSON.parse(errorText)
                error.response.data = errorJson
            } catch (parseError) {
                // If we can't parse as JSON, keep the original blob
                console.warn('Could not parse error blob as JSON:', parseError)
            }
        }

        // get error info
        const statusCode = error?.response?.status
        const originalRequest = error.config
        console.log('error', error)
        switch (statusCode) {
            case 401: {
                const refreshToken = useAuthStore.getState().refreshToken
                localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
                if (refreshToken && !originalRequest._retry && !endpointToIgnore.includes(error.config.url)) {
                    originalRequest._retry = true // prevent infinite retry loop
                    await attemptRefresh(refreshToken)
                    return axiosInstance.request(originalRequest)
                } else {
                    localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
                    useAuthStore.getState().reset()
                    return Promise.reject(error)
                }
            }
            default:
                return Promise.reject(error)
        }
    }
)

export default axiosInstance

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 * */
export const parseAxiosPromise = <T extends ZodSchema>({
    axiosPromise,
    responseShape,
    onZodError
}: {
    axiosPromise: AxiosPromise
    responseShape: T
    onZodError?: (error: ZodError) => void
}): Promise<z.infer<T>> =>
    axiosPromise.then(response => {
        const safeParsedResponseData = responseShape.safeParse(response.data)
        if (!safeParsedResponseData.success) {
            console.error(safeParsedResponseData.error.message + '\n\n\n' + JSON.stringify(response.data))
            onZodError?.(safeParsedResponseData.error)
            throw new Error(safeParsedResponseData.error.message + '\n\n\n' + JSON.stringify(response.data))
        }

        return safeParsedResponseData.data
    })

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 */
export const parseResponseData = <TResponseData extends ZodSchema>({
    responseData,
    responseShape,
    onZodError
}: {
    responseData: unknown
    responseShape: TResponseData
    onZodError?: (error: ZodError) => void
}): z.infer<TResponseData> => {
    const safeParsedResponseData = responseShape.safeParse(responseData)
    if (!safeParsedResponseData.success) {
        console.error(safeParsedResponseData.error.message)
        onZodError?.(safeParsedResponseData.error)
        throw new Error(safeParsedResponseData.error.message)
    }
    return safeParsedResponseData.data
}
